import { useCallback, useEffect, useRef, useState } from "react";

const MIME_CANDIDATES = [
  { mimeType: "audio/webm", extension: "webm" },
  { mimeType: "audio/ogg", extension: "ogg" },
  { mimeType: "audio/mp4", extension: "m4a" },
];

export interface Recording {
  blob: Blob;
  filename: string;
}

const pickMimeType = () => {
  if (typeof MediaRecorder === "undefined") return null;
  return (
    MIME_CANDIDATES.find((candidate) =>
      MediaRecorder.isTypeSupported(candidate.mimeType),
    ) || null
  );
};

export const useAudioRecorder = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isSupported = typeof MediaRecorder !== "undefined";

  const cleanup = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    recorderRef.current = null;
    setIsRecording(false);
    setElapsedSeconds(0);
  }, []);

  useEffect(() => cleanup, [cleanup]);

  const start = useCallback(async () => {
    const candidate = pickMimeType();
    if (!candidate) return false;

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      return false;
    }

    const recorder = new MediaRecorder(stream, { mimeType: candidate.mimeType });
    chunksRef.current = [];
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        chunksRef.current.push(event.data);
      }
    };

    streamRef.current = stream;
    recorderRef.current = recorder;
    recorder.start();

    setIsRecording(true);
    setElapsedSeconds(0);
    intervalRef.current = setInterval(
      () => setElapsedSeconds((previous) => previous + 1),
      1000,
    );

    return true;
  }, []);

  const stop = useCallback(async (): Promise<Recording | null> => {
    const recorder = recorderRef.current;
    if (!recorder) return null;

    const mimeType = recorder.mimeType || "audio/webm";
    const extension =
      MIME_CANDIDATES.find((candidate) =>
        mimeType.startsWith(candidate.mimeType),
      )?.extension || "webm";

    const blob = await new Promise<Blob>((resolve) => {
      recorder.onstop = () =>
        resolve(new Blob(chunksRef.current, { type: mimeType }));
      recorder.stop();
    });

    cleanup();
    chunksRef.current = [];

    if (blob.size === 0) return null;
    return { blob, filename: `recording.${extension}` };
  }, [cleanup]);

  const cancel = useCallback(() => {
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.onstop = null;
      recorder.stop();
    }
    chunksRef.current = [];
    cleanup();
  }, [cleanup]);

  return { isRecording, elapsedSeconds, isSupported, start, stop, cancel };
};
